
import { useState } from "react";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import { ensureValidBoardName } from "@/contexts/boards";

interface EditBoardFormProps {
  boardId: string;
  initialTitle: string;
  initialDescription: string;
  initialColor?: string;
  onComplete: () => void;
}

interface FormData {
  title: string;
  description: string;
  color: string;
}

const DEFAULT_COLOR = "#e74c3c";

const EditBoardForm = ({ 
  boardId, 
  initialTitle, 
  initialDescription, 
  initialColor, 
  onComplete 
}: EditBoardFormProps) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedColor, setSelectedColor] = useState(initialColor || DEFAULT_COLOR);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      title: initialTitle,
      description: initialDescription,
      color: initialColor || DEFAULT_COLOR,
    }
  });

  const onSubmit = async (data: FormData) => {
    if (!user) {
      toast.error("You must be logged in to update a board");
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Use the selected color from the state
      data.color = selectedColor;

      // Ensure the title is valid
      const validatedTitle = ensureValidBoardName(data.title);
      
      const { error } = await supabase
        .from("boards")
        .update({
          title: validatedTitle,
          description: data.description || null,
          color: data.color,
        })
        .eq("id", boardId)
        .eq("user_id", user.id);

      if (error) throw error;

      toast.success("Board updated successfully");
      onComplete();
    } catch (error) {
      console.error("Error updating board:", error);
      toast.error("Failed to update board");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div>
          <label htmlFor="title" className="block text-sm font-medium mb-1">
            Board Name
          </label>
          <Input
            id="title"
            placeholder="My Board"
            {...register("title", { required: "Board name is required" })}
            className={errors.title ? "border-red-500" : ""}
          />
          {errors.title && (
            <span className="text-sm text-red-500">{errors.title.message}</span>
          )}
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium mb-1">
            Description
          </label>
          <Textarea
            id="description"
            rows={3}
            placeholder="Add a description for your board"
            {...register("description")}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Color</label>
          <div className="flex flex-wrap gap-2">
            {["#FF314E", "#EF724A", "#F4CD06", "#2FE161", "#24EDF1", "#3CBEFF", "#0073FF", "#8052F5", "#CD17CE", "#FF00A4"].map((color) => (
              <button
                key={color}
                type="button"
                className={`w-6 h-6 rounded-full border transition-all ${
                  selectedColor === color 
                    ? "ring-2 ring-offset-2 ring-black dark:ring-white" 
                    : "hover:scale-110"
                }`}
                style={{ backgroundColor: color }}
                onClick={() => setSelectedColor(color)}
              />
            ))}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="bg-clustr-blue text-white"
        >
          {isSubmitting ? "Updating Board..." : "Update Board"}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default EditBoardForm;
